import { useEffect, useState } from 'react';
import {
  apiAddUser,
  apiCreateMagicLink,
  apiDeleteUser,
  apiListAdminUsers,
  apiRevokeUserSessions,
  type AdminUserRow,
} from '../api';
import MagicLinkModal from '../components/MagicLinkModal';
import { fmtTime } from '../lib/format';

export default function Admin() {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [userBusyEmail, setUserBusyEmail] = useState<string | null>(null);
  const [userErr, setUserErr] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<
    { email: string; link: string; expires_at: string } | null
  >(null);

  // Add-user form
  const [newEmail, setNewEmail] = useState('');
  const [addBusy, setAddBusy] = useState(false);

  async function loadUsers() {
    try {
      const r = await apiListAdminUsers();
      setUsers(r.users);
    } catch (e) {
      setUserErr(String(e));
    }
  }
  useEffect(() => { loadUsers(); }, []);

  async function generateMagicLink(email: string) {
    setUserErr(null);
    setUserBusyEmail(email);
    try {
      const r = await apiCreateMagicLink(email);
      setMagicLink({ email: r.email, link: r.link, expires_at: r.expires_at });
      await loadUsers();
    } catch (e) {
      setUserErr(String(e));
    } finally {
      setUserBusyEmail(null);
    }
  }

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    setUserErr(null);
    const email = newEmail.trim();
    if (!email) return;
    setAddBusy(true);
    try {
      await apiAddUser(email);
      setNewEmail('');
      await loadUsers();
    } catch (e) {
      setUserErr(String(e));
    } finally {
      setAddBusy(false);
    }
  }

  async function removeUser(email: string) {
    if (!confirm(`確定要移除使用者 ${email}?其 session 與 claim 將一併清除。`)) return;
    setUserBusyEmail(email);
    setUserErr(null);
    try {
      await apiDeleteUser(email);
      await loadUsers();
    } catch (e) {
      setUserErr(String(e));
    } finally {
      setUserBusyEmail(null);
    }
  }

  async function revokeSessions(email: string) {
    if (!confirm(`登出 ${email} 的所有 sessions?\n所有裝置會被強制登出,尚未點擊的登入連結也會失效。使用者仍可重新被產生連結。`)) return;
    setUserBusyEmail(email);
    setUserErr(null);
    try {
      await apiRevokeUserSessions(email);
      await loadUsers();
    } catch (e) {
      setUserErr(String(e));
    } finally {
      setUserBusyEmail(null);
    }
  }

  return (
    <div className="card">
      <h2>管理</h2>

      <section>
        <h3>使用者</h3>

        <form onSubmit={addUser} className="user-add-form">
          <input
            type="email"
            placeholder="新增使用者(電子郵件)"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            disabled={addBusy}
          />
          <button type="submit" disabled={addBusy || !newEmail.trim()}>
            {addBusy ? '新增中…' : '新增'}
          </button>
        </form>

        {userErr && <div className="error">{userErr}</div>}

        <table className="grid">
          <thead>
            <tr>
              <th>Email</th>
              <th>角色</th>
              <th>來源</th>
              <th>標註數</th>
              <th>Session</th>
              <th>最近活動</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const busy = userBusyEmail === u.email;
              const isEnvAdmin = u.source === 'env';
              return (
                <tr key={u.email}>
                  <td>{u.email}</td>
                  <td>
                    {u.is_admin
                      ? <span className="badge badge-active">admin</span>
                      : 'labeler'}
                  </td>
                  <td>
                    <span className="muted small">
                      {isEnvAdmin ? '環境變數' : '管理頁新增'}
                    </span>
                  </td>
                  <td>{u.label_count}</td>
                  <td>
                    {u.active_sessions > 0 ? (
                      <span
                        className="badge badge-active"
                        title="已驗證且未過期的 session 數"
                      >
                        {u.active_sessions} 活躍
                      </span>
                    ) : (
                      <span className="muted small">無</span>
                    )}
                    {u.pending_links > 0 && (
                      <>
                        {' '}
                        <span
                          className="badge badge-deleted"
                          title="已產生但尚未點擊的 magic link 數 (24h 內仍可使用)"
                        >
                          {u.pending_links} 待點擊
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    {u.last_login
                      ? fmtTime(u.last_login)
                      : <span className="muted">尚未登入</span>}
                  </td>
                  <td>
                    <button
                      disabled={busy}
                      onClick={() => generateMagicLink(u.email)}
                    >
                      {busy ? '處理中…' : '產生登入連結'}
                    </button>
                    {(u.active_sessions > 0 || u.pending_links > 0) && (
                      <>
                        {' · '}
                        <button
                          className="linkish"
                          disabled={busy}
                          onClick={() => revokeSessions(u.email)}
                          title="清除所有 sessions(含已驗證 + 待點擊連結)"
                        >
                          登出 sessions
                        </button>
                      </>
                    )}
                    {!isEnvAdmin && (
                      <>
                        {' · '}
                        <button
                          className="linkish"
                          disabled={busy}
                          onClick={() => removeUser(u.email)}
                        >
                          移除
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && <tr><td colSpan={7}>尚無使用者</td></tr>}
          </tbody>
        </table>
      </section>

      {magicLink && (
        <MagicLinkModal
          email={magicLink.email}
          link={magicLink.link}
          expiresAt={magicLink.expires_at}
          onClose={() => setMagicLink(null)}
        />
      )}
    </div>
  );
}
